import React, {useLayoutEffect, useMemo, useRef, useState} from 'react';
import {Table} from 'react-bootstrap';
import {
  Region,
  Service,
  ServiceCategories,
  ServiceRegionStatus,
  ServiceStatus,
} from '../pages/status-dashboard/StatusDashboardContainer';
import {TFunction, useTranslation} from 'react-i18next';
import {
  operational,
  serviceDown,
  informational,
  serviceDisruption,
  serviceNotAvailable,
} from '../assets/icons';
import {ClientUrls, Status} from '../constants';
import {Link} from 'react-router-dom';
import {getMulticloudServiceCategories} from '../util/config';
import formatRegionColumnHeader from '../util/formatRegionColumnHeader';
import '../assets/sass/multicloudStatusDashboard.scss';

interface MulticloudTablesProps {
  regions: Region[];
  serviceCategories: ServiceCategories;
  serviceRegionStatus: ServiceRegionStatus;
}
interface SortedService {
  serviceId: string;
  service: Service;
}

const formatServiceLabel = (label: string): string =>
  label.replace('@AWS', '').replace('@Azure', '').replace('@Google Cloud', '');

const formatServiceCategoryLabel = (label: string): string =>
  label.replace(/\s*@\s*/g, '@');

const getIconElement = (tooltip: string, imgSrc: any, incidentId?: string) =>
  incidentId ? (
    <Link
      data-testid="incident-history-link"
      to={`${ClientUrls.incidentDetailPage}${incidentId}`}>
      <img className="status-icon" src={imgSrc} alt={tooltip} title={tooltip} />
    </Link>
  ) : (
    <img className="status-icon" src={imgSrc} alt={tooltip} title={tooltip} />
  );

const getCellElement = (
  serviceStatus: ServiceStatus,
  t: TFunction<'translation'>,
) => {
  switch (serviceStatus.status) {
    case Status.NormalPerformance:
      return getIconElement(
        t('Common.operational'),
        operational,
        serviceStatus.incidentId,
      );
    case Status.Informational:
      return getIconElement(
        t('Common.informational'),
        informational,
        serviceStatus.incidentId,
      );
    case Status.ServiceDisruption:
      return getIconElement(
        t('Common.serviceDisruption'),
        serviceDisruption,
        serviceStatus.incidentId,
      );
    case Status.ServiceDown:
      return getIconElement(
        t('Common.serviceDown'),
        serviceDown,
        serviceStatus.incidentId,
      );
    default:
      return <div className="status-icon" />;
  }
};

const getStatusElement = (
  serviceStatus: ServiceStatus,
  regionName: string,
  t: TFunction<'translation'>,
) => (
  <div className="service-val" key={regionName}>
    {serviceStatus && serviceStatus.status ? (
      getCellElement(serviceStatus, t)
    ) : (
      <img
        className="status-icon"
        src={serviceNotAvailable}
        alt={t('Common.serviceNotAvailable')}
        title={t('Common.serviceNotAvailable')}
      />
    )}
  </div>
);

const getSortedMulticloudServices = (
  services: Record<string, Service>,
  regionFilter: Region[],
  serviceRegionStatus: ServiceRegionStatus,
): SortedService[] => {
  return Object.keys(services)
    .map((serviceId: string) => ({
      serviceId,
      service: services[serviceId],
    }))
    .filter(({serviceId}) =>
      regionFilter.some(
        (region) => !!serviceRegionStatus?.[serviceId]?.[region.regionId],
      ),
    )
    .sort((a, b) => a.service.serviceName.localeCompare(b.service.serviceName));
};

const getVisibleRegions = (
  services: Record<string, Service>,
  sortedRegions: Region[],
  serviceRegionStatus: ServiceRegionStatus,
): Region[] => {
  return sortedRegions.filter((region) =>
    Object.keys(services).some(
      (serviceId) => !!serviceRegionStatus?.[serviceId]?.[region.regionId],
    ),
  );
};

const MULTICLOUD_CATEGORIES = getMulticloudServiceCategories() as string[];

const getMaxColumnCount = (
  sortedRegions: Region[],
  serviceCategories: ServiceCategories,
  serviceRegionStatus: ServiceRegionStatus,
): number => {
  return Math.max(
    ...MULTICLOUD_CATEGORIES.map((cat) => {
      const services = serviceCategories[cat] || {};
      return getVisibleRegions(services, sortedRegions, serviceRegionStatus)
        .length;
    }),
  );
};

const MulticloudServiceStatusList: React.FC<MulticloudTablesProps> = ({
  regions,
  serviceCategories,
  serviceRegionStatus,
}) => {
  const categoryRefs = useRef<Record<string, HTMLTableCellElement | null>>({});
  const regionHeaderRefs = useRef<Record<string, HTMLTableCellElement | null>>(
    {},
  );
  const [categoryColumnWidth, setCategoryColumnWidth] = useState<number>(0);
  const [regionColumnWidth, setRegionColumnWidth] = useState<number>(0);

  const sortedRegions = useMemo(() => {
    return [...regions].sort((a, b) =>
      a.regionName.localeCompare(b.regionName),
    );
  }, [regions]);

  useLayoutEffect(() => {
    const measuredWidths = MULTICLOUD_CATEGORIES.reduce<number[]>(
      (accumulator, category) => {
        const cell = categoryRefs.current[category];
        if (cell) {
          accumulator.push(cell.scrollWidth);
        }
        return accumulator;
      },
      [],
    );

    if (!measuredWidths.length) {
      return;
    }

    const longestWidth = Math.max(...measuredWidths);
    setCategoryColumnWidth((previousWidth) =>
      previousWidth !== longestWidth ? longestWidth : previousWidth,
    );

    const measuredRegionWidths = Object.values(regionHeaderRefs.current)
      .filter((cell): cell is HTMLTableCellElement => !!cell)
      .map((cell) => cell.getBoundingClientRect().width);

    if (!measuredRegionWidths.length) {
      return;
    }

    const longestRegionWidth = Math.ceil(Math.max(...measuredRegionWidths));
    setRegionColumnWidth((previousWidth) =>
      previousWidth !== longestRegionWidth ? longestRegionWidth : previousWidth,
    );
  }, [regions, serviceCategories, serviceRegionStatus]);

  const maxColumnCount = getMaxColumnCount(
    sortedRegions,
    serviceCategories,
    serviceRegionStatus,
  );

  const {t} = useTranslation();

  return (
    <>
      {MULTICLOUD_CATEGORIES.map((serviceCategory) => {
        const services = serviceCategories[serviceCategory] || {};
        const visibleRegions = getVisibleRegions(
          services,
          sortedRegions,
          serviceRegionStatus,
        );
        const visibleServices = getSortedMulticloudServices(
          services,
          visibleRegions,
          serviceRegionStatus,
        );
        if (!visibleRegions.length || !visibleServices.length) return null;

        const padCount = maxColumnCount - visibleRegions.length;

        return (
          <div
            className="table-container table-responsive status-multicloud-table-container"
            key={serviceCategory}>
            <Table className="status-table-multicloud status-table table-borderless">
              <thead>
                <tr className="status-table-region-header status-table-multicloud-row">
                  <th
                    className="align-middle status-table-multicloud-header status-table-multicloud-first-col"
                    style={
                      categoryColumnWidth
                        ? {minWidth: categoryColumnWidth}
                        : undefined
                    }>
                    {serviceCategory === MULTICLOUD_CATEGORIES[0] &&
                      (t('StatusDashboard.productsServices') as string)}
                  </th>
                  {visibleRegions.map((region: Region) => (
                    <th
                      data-testid="live-status-region-name"
                      key={region.regionName}
                      ref={(element) => {
                        const regionKey = `${serviceCategory}-${region.regionId}`;
                        if (element) {
                          regionHeaderRefs.current[regionKey] = element;
                        } else {
                          delete regionHeaderRefs.current[regionKey];
                        }
                      }}
                      className="align-middle region-col status-table-multicloud-header"
                      style={
                        regionColumnWidth
                          ? {
                              minWidth: regionColumnWidth,
                              width: regionColumnWidth,
                            }
                          : undefined
                      }>
                      {formatRegionColumnHeader(region.regionName)}
                    </th>
                  ))}
                  <th className="status-table-multicloud-header filler-col" />
                  {/* {padCount > 0 &&
                    Array.from({length: padCount}).map((_, idx) => (
                      <th
                        key={`pad-head-${idx}`}
                        className="align-middle region-col blank-region status-table-multicloud-header"></th>
                    ))} */}
                </tr>
              </thead>
              <tbody>
                <tr className="service-category status-table-multicloud-row">
                  <th
                    ref={(element) => {
                      if (element) {
                        categoryRefs.current[serviceCategory] = element;
                      } else {
                        delete categoryRefs.current[serviceCategory];
                      }
                    }}
                    className="category status-multicloud-tooltip status-table-multicloud-header status-table-multicloud-first-col"
                    style={
                      categoryColumnWidth
                        ? {minWidth: categoryColumnWidth}
                        : undefined
                    }>
                    {formatServiceCategoryLabel(serviceCategory)}
                  </th>
                  <td
                    className="status-table-multicloud-cell"
                    colSpan={maxColumnCount + 1}></td>
                </tr>
                {visibleServices.map((sortedService: SortedService) => (
                  <tr
                    className="status-table-multicloud-row"
                    key={sortedService.serviceId}
                    style={{backgroundColor: 'white'}}>
                    <th
                      className="left-heading status-multicloud-tooltip status-table-multicloud-header status-table-multicloud-first-col"
                      style={
                        categoryColumnWidth
                          ? {minWidth: categoryColumnWidth}
                          : undefined
                      }>
                      {formatServiceLabel(sortedService.service.serviceName)}
                    </th>
                    {visibleRegions.map((region: Region) => (
                      <td
                        key={`${sortedService.serviceId}${region.regionName}`}
                        className="align-middle region-col status-table-multicloud-cell"
                        style={
                          regionColumnWidth
                            ? {
                                minWidth: regionColumnWidth,
                                width: regionColumnWidth,
                              }
                            : undefined
                        }>
                        {getStatusElement(
                          serviceRegionStatus[sortedService.serviceId]?.[
                            region.regionId
                          ],
                          region.regionName,
                          t,
                        )}
                      </td>
                    ))}
                    <td className="status-table-multicloud-cell filler-col" />
                    {/* {padCount > 0 &&
                      Array.from({length: padCount}).map((_, idx) => (
                        <td
                          key={`pad-td-${idx}`}
                          className="align-middle region-col blank-region status-table-multicloud-cell"></td>
                      ))} */}
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        );
      })}
    </>
  );
};

export default MulticloudServiceStatusList;
